import { ApiError, apiPath, requestJson } from "./client.js";

/**
 * The console's client for the provider settings routes (`apps/api/src/model-routes.ts`).
 *
 * ## Nothing here can carry a secret back
 *
 * `ProviderView` is the whole shape the server sends, and it has no field that
 * could hold one: a configured provider is identified by its `fingerprint` --
 * derived from ciphertext, never from plaintext -- plus `createdAt`. There is no
 * `reveal` route to call, so this module could not fetch a secret if it wanted
 * to. The one direction plaintext travels is *out*, in `setProviderSecret`'s
 * request body, and nothing here retains it after the call.
 *
 * The connection test is the same story in the other direction: the route
 * returns `{ ok, outcome, reason }` and drops the provider's own text on the
 * line it exists on, so `ConnectionTestResult` cannot carry model output either.
 *
 * ## What the console cannot see
 *
 * `GET /api/v1/model/providers` is driven by the registry, so it answers with
 * the twelve registered providers and nothing else. A stored credential whose
 * provider id had been removed from the registry would not appear in that list
 * and this surface could not revoke it. No provider has ever been removed, so
 * the row cannot exist today; if one is ever removed, the fix belongs in the
 * route (it would have to emit the orphan) and not here, because the console
 * cannot render what it is not sent.
 */

/** `local` is the zero-egress path. Everything else sends source to a third party. */
export type ProviderEgress = "local" | "remote";

export type ProviderStatus = "unconfigured" | "active" | "revoked";

export type ProviderAuthMode = "api-key" | "bearer" | "oauth" | "none";

export interface ProviderCapabilities {
  structuredOutput: boolean;
  streaming: boolean;
  maxInputTokens: number;
}

/**
 * Whether the vendor publishes a host-OAuth flow at all (`supported`), and
 * whether this deployment has registered a client for it (`configured`).
 *
 * The two are kept apart because they are different facts with different fixes.
 * `supported: false` is permanent for `anthropic-oauth` -- Anthropic documents
 * exactly three authentication mechanisms and none of them is host OAuth -- and
 * a console that rendered it as a failed button would be claiming the product
 * was broken when the vendor simply has no such flow.
 */
export interface ProviderOAuthView {
  supported: boolean;
  configured: boolean;
}

export interface ProviderView {
  id: string;
  transport: string;
  authMode: ProviderAuthMode;
  egress: ProviderEgress;
  /** The registry default, or this deployment's override. Never operator input. */
  baseUrl: string;
  capabilities: ProviderCapabilities;
  /** Null for the ten providers that do not authenticate by host OAuth. */
  oauth: ProviderOAuthView | null;
  status: ProviderStatus;
  fingerprint: string | null;
  createdAt: string | null;
  rotatedAt: string | null;
  revokedAt: string | null;
}

export interface ProviderListing {
  /**
   * Whether this deployment booted with an envelope key. False is a supported
   * deployment, not a fault: reads still answer and the zero-egress provider
   * still works, because it holds no credential to encrypt.
   */
  keyConfigured: boolean;
  items: ProviderView[];
}

export interface SetProviderResult {
  outcome: "configured" | "rotated";
  provider: ProviderView;
}

export interface RevokeProviderResult {
  outcome: "revoked";
  provider: ProviderView;
}

export interface ConnectionTestResult {
  ok: boolean;
  outcome: "ok" | "unavailable" | "refused";
  /** Redacted upstream; never carries the credential or the provider's output. */
  reason: string | null;
}

/**
 * Which configured provider to put to work, and which of their models.
 *
 * The same three fields the connection test takes, and stored in the same place:
 * nowhere. `model_credentials` holds custody, not preferences, so the console
 * carries both in component state for the life of the page and says so on the
 * form -- see `ConnectionTestInput` above for why persisting `baseUrl` in the
 * browser was considered and rejected.
 */
export interface ModelAssistInput {
  providerId: string;
  model: string;
  baseUrl?: string | undefined;
  /** Optional evidence-bound question for the finding investigation. */
  question?: string | undefined;
}

/**
 * The result of asking a configured model for prose about a decided finding.
 *
 * `explanation` is deliberately not narrowed to a parsed type here. It is a
 * `model_artifacts` payload -- content that originated outside the trust
 * boundary -- and `ModelExplanationPanel` is where it gets validated, on the
 * same principle the server applies: whoever renders it decides whether it is
 * renderable, and a caller cannot hand the panel a pre-blessed object to skip
 * the check.
 */
export interface ModelExplanationResult {
  outcome: "explained" | "refused" | "unavailable";
  /** Fixed server reason, withheld if it screens as credential-like. */
  reason: string | null;
  /** The `model_artifacts` row, when a provider was actually asked. */
  artifactId: string | null;
  explanation: unknown;
}

export interface AutomaticModelExplanationResult {
  outcome: "explained" | "pending";
  artifactId: string | null;
  explanation: unknown;
}

/**
 * The result of asking a configured model to draft a one-line fix.
 *
 * `outcome` carries the whole story and the console must read it rather than
 * infer from the other fields:
 *
 * - `verified`   — a patch was drafted AND a signal independent of the rule that
 *                  raised the finding confirmed it.
 * - `drafted_unverified` — ASSURE-JS-003 produced an advisory line that silenced
 *                  the detector, but behavioral equivalence still needs human
 *                  review and application tests. It cannot be accepted here.
 * - `rejected`   — a patch was drafted and the check refused it. `patch` is
 *                  `null`, deliberately and permanently.
 * - `refused`    — no safe drafting path was available for this finding.
 * - `unavailable`— the provider could not be reached, or the artifact could not be
 *                  re-read.
 *
 * WHY THE CONSOLE MUST NEVER TRY TO SHOW A REJECTED PATCH
 *
 * The server withholds it on purpose. A rejected line failed the check that the
 * cited credential material is gone, so it may still contain the secret — that
 * is precisely what "rejected" can mean. The line is recorded in the artifact
 * row for audit, and the route does not return it. There is no console path to
 * it and there must not be one: a UI that fetched the artifact to display what
 * the verdict declined to hand over would defeat the check by going around it.
 *
 * An accepted patch is safe to display for the mirror-image reason: acceptance
 * *is* the proof that the credential material is no longer in the line.
 */
export interface DraftedFixResult {
  outcome: "verified" | "drafted_unverified" | "rejected" | "refused" | "unavailable";
  /** Names rules, signals and line numbers. Withheld if it screens as secret-like. */
  reason: string | null;
  /** The `model_artifacts` row, when a provider was actually asked. */
  artifactId: string | null;
  /** Non-null for a verified draft or a visibly unverified advisory draft. */
  patch: { replacementLine: string } | null;
  verdict: {
    accepted: boolean;
    originatingRuleSilenced: boolean;
    signals: readonly { signalId: string; outcome: string }[];
    reason: string;
  } | null;
}

/** The `model_artifacts` row after an operator has ruled on it. */
export interface ModelArtifactView {
  id: string;
  kind: "draft-fix" | "explanation" | "rule-draft";
  status: "recorded" | "stale" | "unavailable";
  humanDecision: "accepted" | "rejected" | null;
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
}

export interface ModelArtifactDecisionResult {
  artifact: ModelArtifactView;
}

/**
 * Model id and base URL are sent per call and stored nowhere.
 *
 * `model_credentials` holds custody, not preferences, and there is no column for
 * either (see `testConnectionSchema` in model-routes.ts). The console therefore
 * keeps both in component state for the life of the page and says so on the
 * form. Persisting them locally was considered and rejected: `baseUrl` decides
 * which host the customer's source would be sent to, and a browser-side store
 * for that would be a security-relevant setting with no server-side record of
 * who set it or when.
 */
export interface ConnectionTestInput {
  model: string;
  baseUrl?: string | undefined;
}

/**
 * The 403 these routes answer to a reviewer or a viewer.
 *
 * Every provider route is owner-gated, reads included, because a listing
 * discloses which third parties this tenant already ships source code to.
 */
export const MODEL_PROVIDER_OWNER_REQUIRED = "model_provider_owner_required";

/**
 * Whether a failure is "you are not an owner" rather than an error.
 *
 * The string is checked, not just the status. `ApiError` fills `code` from a
 * `code` field the provider routes do not send -- they send `{ error }`, which
 * lands in `message` -- so both are accepted. And the console raises its own 403
 * when a session carries no CSRF token; that one must fall through to the
 * ordinary error path, because it is a broken session rather than a role.
 */
export function isOwnerRequired(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 403 &&
    (error.code === MODEL_PROVIDER_OWNER_REQUIRED ||
      error.message === MODEL_PROVIDER_OWNER_REQUIRED)
  );
}

/**
 * Where the browser is sent to begin a host-OAuth flow.
 *
 * A full-page navigation rather than a fetch, because the route answers with a
 * 302 to the vendor's consent screen and sets a state cookie scoped to the
 * callback path. Following that redirect with `fetch` would land the consent
 * page's HTML in a promise and drop the flow on the floor.
 */
export function oauthAuthorizePath(providerId: string): string {
  return apiPath(`/model/oauth/${encodeURIComponent(providerId)}/authorize`);
}

export const modelApi = {
  listProviders(signal?: AbortSignal): Promise<ProviderListing> {
    return requestJson<ProviderListing>(apiPath("/model/providers"), signal ? { signal } : {});
  },

  /**
   * Set or rotate. The server decides which -- `set` is insert-only so that
   * replacing live material is always audited as a rotation -- and reports it
   * back as `outcome`, so the console never has to guess which slot was empty.
   */
  setProviderSecret(
    providerId: string,
    secret: string,
    signal?: AbortSignal,
  ): Promise<SetProviderResult> {
    return requestJson<SetProviderResult>(
      apiPath(`/model/providers/${encodeURIComponent(providerId)}`),
      {
        method: "PUT",
        body: JSON.stringify({ secret }),
        headers: { "Content-Type": "application/json" },
        ...(signal ? { signal } : {}),
      },
    );
  },

  revokeProvider(providerId: string, signal?: AbortSignal): Promise<RevokeProviderResult> {
    return requestJson<RevokeProviderResult>(
      apiPath(`/model/providers/${encodeURIComponent(providerId)}`),
      {
        method: "DELETE",
        ...(signal ? { signal } : {}),
      },
    );
  },

  getAutomaticExplanation(
    findingId: string,
    signal?: AbortSignal,
  ): Promise<AutomaticModelExplanationResult> {
    return requestJson<AutomaticModelExplanationResult>(
      apiPath(`/findings/${encodeURIComponent(findingId)}/automatic-model-enrichment`),
      signal ? { signal } : {},
    );
  },

  /**
   * Ask a configured model to explain a finding whose verdict is already settled.
   *
   * A `POST` rather than a `GET` because it is not a read: it spends the
   * tenant's money and sends the finding's title and description to a third
   * party. The route sends bounded recorded metadata, source path, and the
   * scanner-redacted cited snippet; it never sends raw evidence or row IDs.
   *
   * Every outcome comes back as a 200 carrying an `outcome`, including "the
   * provider is down" and "this finding has no verdict yet". Neither is an error
   * the console should render as a broken page.
   */
  explainFinding(
    findingId: string,
    input: ModelAssistInput,
    signal?: AbortSignal,
  ): Promise<ModelExplanationResult> {
    return requestJson<ModelExplanationResult>(
      apiPath(`/findings/${encodeURIComponent(findingId)}/model-explanation`),
      {
        method: "POST",
        body: JSON.stringify({
          providerId: input.providerId,
          model: input.model,
          ...(input.baseUrl ? { baseUrl: input.baseUrl } : {}),
          ...(input.question ? { question: input.question } : {}),
        }),
        headers: { "Content-Type": "application/json" },
        ...(signal ? { signal } : {}),
      },
    );
  },

  /**
   * Ask a configured model for a one-line fix, verified before it is offered.
   *
   * Unlike `explainFinding`, this one sends the cited line of source to the
   * provider — that is unavoidable, because a fix for a line cannot be drafted
   * without it, and it is why the route is owner-gated and rate-limited. The
   * console must say so before the operator presses anything.
   *
   * Every outcome is a 200 with an `outcome` field, refusals included. A finding
   * whose class has no independent signal is the common case and is not an error.
   */
  draftFix(
    findingId: string,
    input: ModelAssistInput,
    signal?: AbortSignal,
  ): Promise<DraftedFixResult> {
    return requestJson<DraftedFixResult>(
      apiPath(`/findings/${encodeURIComponent(findingId)}/model-drafted-fix`),
      {
        method: "POST",
        body: JSON.stringify(
          input.baseUrl
            ? { providerId: input.providerId, model: input.model, baseUrl: input.baseUrl }
            : { providerId: input.providerId, model: input.model },
        ),
        headers: { "Content-Type": "application/json" },
        ...(signal ? { signal } : {}),
      },
    );
  },

  /**
   * Record an operator's accept/reject on a drafted fix.
   *
   * The decision is final: the server answers 409 to a second one, because an
   * artifact that could be re-decided is not a record of who decided it. The
   * console disables the controls once a decision comes back rather than relying
   * on that, so the ordinary path never provokes the conflict.
   *
   * "Accepted" means the operator judged the line worth applying. It does NOT
   * apply anything -- no repository is written, no remediation proposal is
   * created. They copy the line themselves.
   */
  decideArtifact(
    artifactId: string,
    decision: "accepted" | "rejected",
    signal?: AbortSignal,
  ): Promise<ModelArtifactDecisionResult> {
    return requestJson<ModelArtifactDecisionResult>(
      apiPath(`/model/artifacts/${encodeURIComponent(artifactId)}/decision`),
      {
        method: "POST",
        body: JSON.stringify({ decision }),
        headers: { "Content-Type": "application/json" },
        ...(signal ? { signal } : {}),
      },
    );
  },

  testProvider(
    providerId: string,
    input: ConnectionTestInput,
    signal?: AbortSignal,
  ): Promise<ConnectionTestResult> {
    return requestJson<ConnectionTestResult>(
      apiPath(`/model/providers/${encodeURIComponent(providerId)}/test`),
      {
        method: "POST",
        body: JSON.stringify(
          input.baseUrl ? { model: input.model, baseUrl: input.baseUrl } : { model: input.model },
        ),
        headers: { "Content-Type": "application/json" },
        ...(signal ? { signal } : {}),
      },
    );
  },
};
